import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import VideoCard from "@/components/feature/VideoCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import ScrollRow from "@/pages/home/components/ScrollRow";
import { useSEO } from "@/hooks/useSEO";
import { categoryList, videoList } from "@/mocks/videos";

function SectionHeader({
  title,
  subtitle,
  to,
}: {
  title: string;
  subtitle: string;
  to?: string;
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">
          {title}
        </h2>
        <p className="mt-1 text-sm text-foreground-500">{subtitle}</p>
      </div>
      {to && (
        <Link
          to={to}
          className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          查看全部
          <i className="ri-arrow-right-s-line text-base" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  useSEO({
    title: "桔子视频正版影视资源在线播放与视频分享平台",
    description:
      "桔子视频为您提供丰富的影视视频内容，涵盖电影、电视剧、综艺等多类别，支持在线观看和视频分享，带来优质视听体验。",
    keywords: "桔子视频,视频平台,在线影视,电影资源,电视剧,视频分享,影视观看",
  });

  const newReleases = [...videoList].sort((a, b) => b.year - a.year).slice(0, 6);
  const latest = [...videoList].sort((a, b) => b.year - a.year).slice(0, 10);
  const ranking = [...videoList].sort((a, b) => b.rating - a.rating).slice(0, 8);

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
        <HeroCarousel />
      </div>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <SectionHeader title="热门分类" subtitle="按类别快速探索你感兴趣的影视内容" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
          {categoryList.map((category) => (
            <Link
              key={category.key}
              to={`/category/${category.key}`}
              className="group flex flex-col items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/60 md:p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={category.icon} />
              </span>
              <div>
                <h3 className="text-base font-semibold text-foreground-950">
                  {category.name}
                </h3>
                <p className="mt-1 line-clamp-2 text-xs text-foreground-500">
                  {category.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 py-10 md:py-12">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeader
            title="新片推荐"
            subtitle="精选近期高分新作，第一时间发现好内容"
            to="/category/movie"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {newReleases.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <SectionHeader title="最新上线" subtitle="刚刚上线的影视作品，左右滑动浏览更多" />
        <ScrollRow>
          {latest.map((video) => (
            <div key={video.id} className="w-[150px] shrink-0 sm:w-[170px]">
              <VideoCard video={video} />
            </div>
          ))}
        </ScrollRow>
      </section>

      <section className="bg-background-100/70 py-10 md:py-12">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeader title="热门榜单" subtitle="根据播放热度与评分综合排序，看看大家都在追什么" />
          <ScrollRow>
            {ranking.map((video, index) => (
              <Link
                key={video.id}
                to={`/detail/${video.id}`}
                className="group relative w-[150px] shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:w-[170px]"
              >
                <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
                  <img
                    src={video.cover}
                    alt={`${video.title} 封面`}
                    title={`${video.title} 在线播放`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-foreground-950/70 text-sm font-bold text-background-50">
                    {index + 1}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="truncate text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {video.title}
                  </h3>
                  <div className="mt-1.5 flex items-center justify-between text-xs text-foreground-500">
                    <span>{video.typeName}</span>
                    <span className="flex items-center gap-1 text-primary-600">
                      <i className="ri-star-fill text-sm" />
                      {video.rating.toFixed(1)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </ScrollRow>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">
              关于桔子视频
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              桔子视频是一个专注于正版影视资源的在线播放与视频分享平台。我们汇聚电影、电视剧、综艺、动漫、纪录片等多类别优质内容，致力于为影视爱好者提供便捷的查找体验、流畅的观看体验，以及自由分享与发现的社区氛围。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              无论你是想追一部热播剧、看一场热血大片，还是与大家分享你的观影心得，桔子视频都希望成为你随身的视听伙伴。平台所有影视内容均为演示数据，仅用于产品展示。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                了解我们
              </Link>
              <Link
                to="/share-intro"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                我要分享视频
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-accent-100/70 p-4">
              <p className="text-2xl font-bold text-accent-700 font-heading">5000+</p>
              <p className="mt-1 text-xs text-foreground-600">正版影视内容</p>
            </div>
            <div className="rounded-lg bg-primary-100/70 p-4">
              <p className="text-2xl font-bold text-primary-700 font-heading">200万+</p>
              <p className="mt-1 text-xs text-foreground-600">日活跃观众</p>
            </div>
            <div className="rounded-lg bg-secondary-100 p-4">
              <p className="text-2xl font-bold text-foreground-900 font-heading">1080P</p>
              <p className="mt-1 text-xs text-foreground-600">高清流畅播放</p>
            </div>
            <div className="rounded-lg bg-primary-100/70 p-4">
              <p className="text-2xl font-bold text-primary-700 font-heading">4.9</p>
              <p className="mt-1 text-xs text-foreground-600">用户评分</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}